import { Button, Drawer, Flex, message } from "antd";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { isEligibleForOnboarding } from "../../../domain/user.domain";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux-hooks";
import { createCustomNote, getCustomNoteList, updateCustomNote } from "../../../service/notes.service";
import {
  setAITemplateModalState,
  setCustomTemplateSelectedTab,
  setStoredTemplateData,
  setTemplateOpenedFromNoteModal,
} from "../../../slices/appStateSlice";
import { setRecordingDetail } from "../../../slices/recordingsSlice";
import Create, { type CreateFormRef } from "./Create";
import ListContainer from "./ListContainer";
import { type CustomNote, TabType } from "./types";

export const AITemplateModal = () => {
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const dispatch = useAppDispatch();
  const { isAITemplateModalVisible, storedTemplateData, customTemplateSelectedTab } = useAppSelector(
    (state) => state.appState,
  );
  const [currentTemplate, setCurrentTemplate] = React.useState<CustomNote | null>(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const templateOpenedFromNoteModal = useAppSelector((state) => state.appState.templateOpenedFromNoteModal);
  const { recordingDetail } = useAppSelector((state) => state.recordings);
  const [isLoading, setIsLoading] = useState(false);
  const [customNoteList, setCustomNoteList] = useState<CustomNote[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [focusInstructions, setFocusInstructions] = useState(false);
  const needsOnboarding = isEligibleForOnboarding(userInfo);
  const createFormRef = useRef<CreateFormRef>(null);

  const fetchCustomNoteList = useCallback(async () => {
    try {
      const response = await getCustomNoteList();
      setCustomNoteList(response.data);
      setInitialLoading(false);
    } catch (error) {
      console.error("Error fetching custom note list:", error);
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomNoteList();
  }, [isAITemplateModalVisible]);

  React.useEffect(() => {
    if ((needsOnboarding && userInfo.onboarding_data) || !needsOnboarding) {
      if (storedTemplateData) {
        dispatch(setCustomTemplateSelectedTab(TabType.CREATE));
        const appendInstructions = storedTemplateData.appendInstructions || "";
        setFocusInstructions(!!appendInstructions || !!storedTemplateData.focusInstructions);
        if (storedTemplateData.id && !storedTemplateData.isFromTemplate) {
          setIsEditing(true);
          const existingPrompt = storedTemplateData.prompt || "";
          const combinedPrompt = appendInstructions
            ? existingPrompt
              ? `${existingPrompt}\n\n${appendInstructions}`
              : appendInstructions
            : existingPrompt;
          setCurrentTemplate({
            ...storedTemplateData,
            prompt: combinedPrompt,
          } as CustomNote);
        } else {
          setIsEditing(false);
          setCurrentTemplate({
            name: storedTemplateData.name,
            document: storedTemplateData.document,
            prompt: appendInstructions,
            from_template: storedTemplateData.id,
            settings: {
              is_addon: false,
              is_cpt_codes: false,
            },
          } as CustomNote);
        }
        dispatch(setAITemplateModalState(true));
        dispatch(setStoredTemplateData(null));
      }
    }
  }, [storedTemplateData, dispatch, userInfo, needsOnboarding]);

  const handleDrawerClose = () => {
    resetStateToDefault(true);
  };

  function handleEditTemplate(template: CustomNote) {
    setIsEditing(true);
    setCurrentTemplate(template);
    dispatch(setCustomTemplateSelectedTab(TabType.CREATE));
  }

  async function handleGoBack() {
    resetStateToDefault();
    await fetchCustomNoteList();
  }

  const handleSaveTemplate = async () => {
    // Validate form before saving
    if (createFormRef.current && !createFormRef.current.validateForm()) {
      message.error("Please fill in all the required fields");
      return;
    }
    try {
      if (currentTemplate?.name && currentTemplate?.name.length > 100) {
        message.error("Template name should be less than 100 characters");
        return;
      }
      setIsLoading(true);
      if (currentTemplate) {
        if (isEditing) {
          const res = await updateCustomNote(currentTemplate.pk, {
            name: currentTemplate.name,
            document: currentTemplate.document,
            prompt: currentTemplate.prompt,
            settings: currentTemplate.settings,
            is_group_accessible: currentTemplate.is_group_accessible,
          });
          if (res.data.status === 2) {
            message.error("Error in updating template. Please try again.");
          } else {
            await fetchCustomNoteList();
            message.success("Your template was successfully updated");
          }
        } else {
          const { name, document, prompt, settings, from_template, is_group_accessible } = currentTemplate;
          const res = await createCustomNote({ name, document, prompt, settings, from_template, is_group_accessible });
          if (res.data.status === 2) {
            message.error("Error in creating template. Please try again.");
          } else {
            await fetchCustomNoteList();
            message.success(`Your template ${name} was successfully created`);
          }
          if (templateOpenedFromNoteModal) {
            dispatch(setTemplateOpenedFromNoteModal(false));
            dispatch(setAITemplateModalState(false));
            dispatch(
              setRecordingDetail({
                ...recordingDetail,
                note_type: res.data.type_id,
                note_type_info: {
                  id: res.data.type_id,
                  name: res.data.name,
                },
              }),
            );
          }
        }
        resetStateToDefault();
      }
    } catch (err) {
      console.error(err);
      message.error(isEditing ? "Failed to update template" : "Failed to create template");
    } finally {
      setIsLoading(false);
    }
  };

  const resetStateToDefault = (withclose = false) => {
    if (withclose) {
      dispatch(setAITemplateModalState(!withclose));
    }
    dispatch(setCustomTemplateSelectedTab(TabType.LIST));
    setCurrentTemplate(null);
    setIsEditing(false);
    setFocusInstructions(false);
  };

  // Determine which footer to show
  const getDrawerFooter = () => {
    if (customTemplateSelectedTab === TabType.CREATE) {
      return (
        <Flex gap={16} justify={"flex-end"} style={{ padding: "0px 10px" }}>
          <Flex gap={10}>
            <Button type="default" onClick={handleGoBack}>
              Go back
            </Button>
            <Button type="primary" onClick={handleSaveTemplate} loading={isLoading}>
              {isEditing ? "Save changes" : "Create Template"}
            </Button>
          </Flex>
        </Flex>
      );
    }

    // LIST tab
    if (customNoteList.length === 0) return null;
    return (
      <Flex gap={16} justify="flex-end">
        <Button type="default" onClick={() => dispatch(setAITemplateModalState(false))}>
          Close
        </Button>
        <Button
          type="primary"
          onClick={() => {
            dispatch(setCustomTemplateSelectedTab(TabType.CREATE));
          }}
        >
          Create Template
        </Button>
      </Flex>
    );
  };

  return (
    <Drawer
      closable={false}
      open={isAITemplateModalVisible}
      onClose={handleDrawerClose}
      width={750}
      styles={{
        body: {
          padding: "0",
        },
      }}
      footer={getDrawerFooter()}
    >
      <>
        {customTemplateSelectedTab === TabType.LIST ? (
          <ListContainer
            handleEditTemplate={handleEditTemplate}
            customNoteList={customNoteList}
            fetchCustomNoteList={fetchCustomNoteList}
            isListLoading={initialLoading}
          />
        ) : (
          <Create
            ref={createFormRef}
            setCurrentTemplate={setCurrentTemplate}
            isEditing={isEditing}
            currentTemplate={currentTemplate}
            handleGoBack={handleGoBack}
            focusInstructions={focusInstructions}
          />
        )}
      </>
    </Drawer>
  );
};
